// src/task/TaskBoard.js
import React, { useState, useEffect, useRef } from 'react';
import TaskColumn from './TaskColumn';
import { DragDropContext } from '@hello-pangea/dnd';
import { useOutletContext } from 'react-router-dom';
import { motion } from "framer-motion";
import logo from "../../image/progress1.gif";
import api from '../../api';

import axios from 'axios';

const ConfirmationModal = ({ isOpen, onConfirm, onCancel, title, message, confirmText, cancelText, type = 'warning' }) => {
  if (!isOpen) return null;

  const getIconAndColors = () => {
    switch (type) {
      case 'success':
        return {
          icon: '✓',
          bgColor: 'bg-green-50',
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          confirmBg: 'bg-green-600 hover:bg-green-700'
        };
      case 'danger':
        return {
          icon: '⚠',
          bgColor: 'bg-red-50',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          confirmBg: 'bg-red-600 hover:bg-red-700'
        };
      case 'info':
        return {
          icon: 'ℹ',
          bgColor: 'bg-blue-50',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          confirmBg: 'bg-blue-600 hover:bg-blue-700'
        };
      case 'move':
        return {
          icon: '↗',
          bgColor: 'bg-purple-50',
          iconBg: 'bg-purple-100',
          iconColor: 'text-purple-600',
          confirmBg: 'bg-purple-600 hover:bg-purple-700'
        };
      default: // warning
        return {
          icon: '⚠',
          bgColor: 'bg-amber-50',
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600',
          confirmBg: 'bg-amber-600 hover:bg-amber-700'
        };
    }
  };

  const { icon, bgColor, iconBg, iconColor, confirmBg } = getIconAndColors();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div className={`mx-auto flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0 sm:h-12 sm:w-12`}>
            <span className={`text-2xl font-bold ${iconColor} sm:text-xl`}>
              {icon}
            </span>
          </div>
          
          <div className="mt-4 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <h3 className="text-xl font-semibold leading-6 text-gray-900 sm:text-lg">
              {title}
            </h3>
            <div className="mt-3">
              <p className="text-sm text-gray-600 leading-relaxed">
                {message}
              </p>
            </div>
          </div>
          
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors sm:mt-0 sm:w-auto"
              onClick={onCancel}
            >
              {cancelText || 'Annuler'}
            </button>
            <button
              type="button"
              className={`inline-flex w-full justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors sm:w-auto ${confirmBg}`}
              onClick={() => onConfirm?.()}
            >
              {confirmText || 'Confirmer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

 const TaskBoard = () => {
   const {
     searchTerm,
     dateStart,
     dateEnd,
     priorityFilter,
     typeFilter,
     moduleFilter,         // ← récupéré ici
     organisationFilter,
     deadlineTechFilter,
     deadlineClientFilter,
     developpeurs
   } = useOutletContext();

  const [allStages, setAllStages] = useState([]);
  const [permittedStages, setPermStages] = useState([]);
  const [tasks, setTasks] = useState({});
  const [pendingUpdates, setPendingUpdates] = useState(new Set());
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });
  const isMovingRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [causeText, setCauseText] = useState('');
  const [pendingMove, setPendingMove] = useState(null); // { ticket, source, destination }
  const [utilisateur_id, setUtilisateur_id] = useState(null); // { ticket, source, destination }
  const [roleId, setRoleId] = useState(null); // { ticket, source, destination }
  const [isMobile, setIsMobile] = useState(null);
    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
  
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setUtilisateur_id(parseInt(user.id, 10));
        setRoleId(parseInt(user.role_id, 10));

      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);


  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      type: 'warning'
    });
  };

  // 1) Charger toutes les étapes
  useEffect(() => {
    api.get('/parametrages/etapes')
      .then(res => {
        const etapes = res.data.etapes || [];
        etapes.sort((a, b) => a.order - b.order);
        setAllStages(etapes);
      })
      .catch(err => console.error('GET /etapes error:', err));
  }, []);

  // 2) Étapes autorisées pour le rôle
  useEffect(() => {
    if (!roleId || allStages.length === 0) return;

    api.get(`/parametrages/roles/${roleId}`)
      .then(res => {
        const role = res.data.role || res.data;
        const allowedIds = Array.isArray(role.etapes) ? role.etapes.map(e => e.id) : [];
        const filtered = allStages
          .filter(stage => allowedIds.includes(stage.id))
          .sort((a, b) => a.order - b.order);
        setPermStages(filtered);
      })
      .catch(err => console.error('Erreur GET /roles/{id} :', err));
  }, [roleId, allStages]);

  // 3) Charger et grouper les tickets clientes
  useEffect(() => {
    if (permittedStages.length === 0) return;
    const fetchTickets = () => {
      api.get(`/tickets/developpeur/${utilisateur_id}`)
        .then(res => {
          const grouped = {};
          (res.data.tickets || []).forEach(ticket => {
            if (String(ticket.confirmation_admin) !== String(1) && String(ticket.accepter) !== String(1) && String(ticket.validation_adminCl) !== String(4)) {
              return; // Ne pas afficher ce ticket
            }
            let sid = ticket.etape_id != null
              ? Number(ticket.etape_id)
              : permittedStages[0].id;
            if (!permittedStages.some(s => s.id === sid)) {
              sid = permittedStages[0].id;
            }
            grouped[sid] = grouped[sid] || [];
            grouped[sid].push({
              id: ticket.id,
              title: ticket.nom,
              content: ticket.content,
              type: ticket.type,
              deadlineClient: ticket.deadline_client,
              deadlineTech: ticket.deadline_tech, // ✅ Ajouté deadline_tech (peut être null)
              developpeurs: ticket.developpeurs,
              priorite: ticket.priorite,
              status: ticket.status,
              createur: ticket.createur
                ? `${ticket.createur.prenom} ${ticket.createur.nom}`
                : '-',
              etape: sid, // ✅ Utilise sid qui est l'ID numérique de l'étape
              // ✅ Ajout de champs supplémentaires pour debug
              organisation: ticket.organisation,
              module: ticket.module,
              module_id: ticket.module_id,            // <-- AJOUT

            });
          });
          setTasks(grouped);
        })
        .catch(err => console.error('GET /tickets error:', err));
    };
    fetchTickets();
    const interval = setInterval(fetchTickets, 5000);
    return () => clearInterval(interval);
  }, [permittedStages, utilisateur_id]);

  // 4) Drag & drop avec MAJ backend
  const needsConfirmation = (sourceStageId, destStageId) => {
    const destStage = allStages.find(s => s.id === destStageId);
    console.log(">> ÉTAPE DESTINATION :", destStage?.nom);
    const criticalStages = ['LIVREE', 'TERMINE', 'VALIDE', 'DÉPLOYE', 'PRODUCTION'];
    return criticalStages.some(stage => destStage?.nom?.toUpperCase().includes(stage));
  };

  const getModalType = (destStageId) => {
    const destStage = allStages.find(s => s.id === destStageId);
    const name = destStage?.nom?.toUpperCase() || '';
    if (name.includes('LIVRÉE')) return 'success';
    if (name.includes('VALIDÉ')) return 'info';
    if (name.includes('REJETÉ')) return 'danger';
    return 'move';
  };

  const handleConfirmReject = () => {
  if (!pendingMove || !causeText.trim()) return;
  setIsProcessing(true);

  api.post(`/tickets/${pendingMove.ticket.id}`, {
    etape_id: 8,
    raison_retour: causeText.trim(),
    modifier: parseInt(utilisateur_id, 10)
  })
  .then((res) => {
    // Mettre à jour les listes localement
    const srcList = Array.from(tasks[pendingMove.source] || []);
    const dstList = Array.from(tasks[pendingMove.destination] || []);

    const index = srcList.findIndex(t => t.id === pendingMove.ticket.id);
    if (index !== -1) {
      const movedTicket = { ...pendingMove.ticket, etape: 8 };
      srcList.splice(index, 1);
      dstList.unshift(movedTicket);

      setTasks(prev => ({
        ...prev,
        [pendingMove.source]: srcList,
        [pendingMove.destination]: dstList
      }));
    }
  })
  .catch((err) => {
    console.error("Erreur retour fonctionnel:", err);
  })
  .finally(() => {
    setIsProcessing(false);
    setShowReturnModal(false);
    setCauseText('');
    setPendingMove(null);
  });
};


  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const src = Number(source.droppableId);
    const dst = Number(destination.droppableId);
    if (src === dst) return;

    const srcList = Array.from(tasks[src] || []);
    const dstList = Array.from(tasks[dst] || []);
    const moved = srcList[source.index];
    if (!moved) return;
     if (dst === 8) {
    // Étape Retour Fonctionnel
    setPendingMove({ ticket: moved, source: src, destination: dst });
    setShowReturnModal(true);
    return;
  }

    const performMove = () => {
      if (isMovingRef.current) return;
      isMovingRef.current = true;

      const [movedTask] = srcList.splice(source.index, 1);
      movedTask.etape = dst;
      dstList.splice(destination.index, 0, movedTask);

      setTasks(prev => ({ ...prev, [src]: srcList, [dst]: dstList }));
      setPendingUpdates(prev => new Set([...prev, movedTask.id]));

      const destStage = allStages.find(s => s.id === dst);
      if (destStage?.nom?.toUpperCase().includes('LIVRÉE')) {
        localStorage.setItem('last_livree_ticket_id', movedTask.id);
      }

      api.post(`/tickets/${movedTask.id}`, { etape_id: dst, status: 1, modifier: parseInt(utilisateur_id, 10),testeur_id: parseInt(utilisateur_id, 10)})
        .then(res => {
          console.log('✅ Ticket mis à jour:', res.data);
        })
        .catch(err => {
          console.error('Erreur mise à jour:', err);
          // rollback
          const rollbackList = Array.from(tasks[dst] || []);
          const index = rollbackList.findIndex(t => t.id === movedTask.id);
          if (index !== -1) rollbackList.splice(index, 1);
          srcList.splice(source.index, 0, movedTask);
          setTasks(prev => ({ ...prev, [src]: srcList, [dst]: rollbackList }));
        })
        .finally(() => {
          setPendingUpdates(prev => {
            const newSet = new Set(prev);
            newSet.delete(movedTask.id);
            return newSet;
          });
          closeConfirmationModal();
          isMovingRef.current = false;
        });
    };

    if (needsConfirmation(src, dst)) {
      const taskTitle = moved.title || `Ticket #${moved.id}`;
      const destStage = allStages.find(s => s.id === dst);
      setConfirmationModal({
        isOpen: true,
        title: 'Confirmer l\'action',
        message: `Voulez-vous vraiment déplacer le ticket "${taskTitle}" vers l'étape "${destStage?.nom}" ?`,
        onConfirm: () => {
          console.log("✅ Confirmation cliquée !");
          performMove();
        },
        type: getModalType(dst)
      });
    } else {
      performMove();
    }
  };

  // 5) Filtrer les tâches selon les critères
  const filteredTasks = permittedStages.reduce((acc, stage) => {
      acc[stage.id] = (tasks[stage.id] || []).filter(task => {
        // Filtre texte (inchangé)
        const textMatch = !searchTerm || 
        [task.title, task.content, task.module?.nom, task.organisation?.nomOrganisation, task.type]
        .some(field => field && field.toString().toLowerCase().includes(searchTerm.toLowerCase()));

        // Filtre plage de dates client (inchangé)
        const deadlineDate = task.deadlineClient
          ? new Date(`${task.deadlineClient}T00:00:00`)
          : null;
        let dateMatch = true;
        if (dateStart) {
          const start = new Date(`${dateStart}T00:00:00`);
          dateMatch = dateMatch && (!!deadlineDate && deadlineDate >= start);
        }
        if (dateEnd) {
          const end = new Date(`${dateEnd}T23:59:59`);
          dateMatch = dateMatch && (!!deadlineDate && deadlineDate <= end);
        }

        // Filtre priorité / type (inchangé)
        const priorityMatch = !priorityFilter || task.priorite === priorityFilter;
        const typeMatch     = !typeFilter     || task.type     === typeFilter;

        // Filtre module / organisation (usage de .id)
        const moduleMatch = !moduleFilter || Number(task.module_id) === Number(moduleFilter);
        console.log(">> Module Match :", moduleMatch, "pour le module_id :", task.module_id, "et le filtre :", moduleFilter);
        const organisationMatch = !organisationFilter || (task.organisation?.id == organisationFilter);

        // Filtre échéance technique
        const techMatch = !deadlineTechFilter ||
          task.deadlineTech === deadlineTechFilter;

        // Filtre échéance client (exact match si rempli)
        const clientMatch = !deadlineClientFilter ||
          task.deadlineClient === deadlineClientFilter;

        return textMatch
          && dateMatch
          && priorityMatch
          && typeMatch
          && moduleMatch
          && organisationMatch
          && techMatch
          && clientMatch;
      });
      return acc;
    }, {});

  return (
    <div div className="flex gap-4 p-4 h-[calc(100vh-64px)] overflow-x-auto">
            {isProcessing && (
  <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[60]">
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className="bg-white/90 rounded-xl p-8 shadow-lg border border-terrecuite-100 max-w-sm w-full mx-4"
    >
      <div className="flex flex-col items-center text-center space-y-5">
        {/* Logo animé avec conteneur ajusté */}
<motion.div 
          className="w-20 h-20 mb-3"
          animate={{ 
            scale: [1, 1.1, 1]
          }}
          transition={{
            scale: {
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse"
            }
          }}
        >
          <img 
            src={logo}
            alt="Chargement en cours"
            className="w-full h-full object-contain"
          />
        </motion.div>
        
        {/* Texte avec hiérarchie visuelle améliorée */}
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-terrecuite-700">
            Traitement en cours...
          </h3>
          <p className="text-terrecuite-500/90 text-sm leading-relaxed">
            Nous traitons votre demande, merci de patienter.
          </p>
        </div>

        {/* Barre de progression subtile avec plus de transparence */}
        <div className="w-full bg-terrecuite-100/50 rounded-full h-1.5 mt-2">
          <motion.div
            className="bg-terrecuite-500/80 h-full rounded-full"
            initial={{ width: 0 }}
            animate={{ width: ["0%", "70%", "100%", "0%"] }}
            transition={{ 
              duration: 3, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
          />
        </div>
      </div>
    </motion.div>
  </div>
)}
      {showReturnModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
    >
      <div className="flex items-center mb-4">
        <span className="text-red-500 text-xl mr-3">⚠</span>
        <h3 className="text-lg font-semibold text-gray-800">Retour Fonctionnel</h3>
      </div>
      <p className="text-gray-600 mb-3">
        Veuillez saisir une raison pour retourner le ticket <strong>"{pendingMove?.ticket?.nom}"</strong>.
      </p>

      <textarea
        className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
        rows={4}
        placeholder="Raison du retour..."
        value={causeText}
        onChange={(e) => setCauseText(e.target.value)}
        disabled={isProcessing}
      />

      <div className="flex justify-end space-x-3">
        <button
          onClick={() => { setShowReturnModal(false); setCauseText(''); }}
          disabled={isProcessing}
          className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Annuler
        </button>
        <button
          disabled={!causeText.trim() || isProcessing}
          onClick={handleConfirmReject}
          className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2 ${
            causeText.trim() && !isProcessing
              ? "bg-red-600 hover:bg-red-700"
              : "bg-red-300 cursor-not-allowed"
          }`}
        >
          {isProcessing ? (
            <>
              <motion.div
                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              Traitement...
            </>
          ) : (
            "Retourner"
          )}
        </button>
      </div>
    </motion.div>
  </div>
)}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={confirmationModal.onConfirm}
        onCancel={closeConfirmationModal}
        type={confirmationModal.type}
      />

     <DragDropContext onDragEnd={onDragEnd}>
        <div className={`p-4 overflow-x-auto min-h-[calc(100vh-64px)] ${isMobile ? 'flex flex-col gap-3' : 'flex gap-4'}`}>
          {permittedStages.map(stage => (
            <TaskColumn
              key={stage.id}
              droppableId={String(stage.id)}
              title={stage.nom}
              count={filteredTasks[stage.id]?.length || 0}
              tasks={filteredTasks[stage.id] || []}
              couleur={stage.couleur}
              isMobile={isMobile}
            />
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};

export default TaskBoard;